import { useAgentLogState } from "@/modules/app/states/agentLog";
import {
  type AgentSession,
  AgentSessionStepType,
  type DoraAgentSession,
  FairAgentSession,
  type MultiVendorAgentSession,
  type QaAgentSession,
  type RiskAgentSession,
  type SOC2GapAgentSession,
} from "@/modules/sessions/types";
import {
  handleContinueDoraGapAssessment,
  handleContinueFairAssessment,
  handleContinueMultiVendorAssessment,
  handleContinueQuestionnaire,
  handleContinueRiskAssessment,
  handleContinueSoc2GapAssessment,
} from "@/modules/sessions/use-cases/agent-session.use-case";
import { ROUTES } from "@/shared/constants/routes";
import StopwatchIcon from "@/shared/icons/stop-watch.tsx";
import { addNotification } from "@/shared/states/notification";
import { Button } from "@nextui-org/react";
import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { helper } from "../../constants/helpers";
import { getAgentData, getAgentTypeFromId } from "../../states";
import { AGENT_TYPES, RiskAssessmentTypes } from "../../types/index.ts";
import { generateAndDownloadSessionReport, getFileNameForDownloadReport } from "../../utils/downloadReport.ts";
import DiscardSessionModal from "./DiscardSessionModal"; // Import the modal
import { SendThirdPartyVendorEmailPayload } from "../../types/vendorIntakeForm.ts";
import { useSendThirdPartyVendorFormEmail, useGetVendorIntakeFormLink, useDiscardAgentSession } from "@/modules/vendors/requests/index.ts";

const getFileName = (agentType: AGENT_TYPES) => {
  switch (agentType) {
    case AGENT_TYPES.QUESTIONNAIRE:
      return "questionnaire";
    case AGENT_TYPES.GAP_ASSESSMENT:
      return "gap_assessment";
    default:
      return "risk_assessment";
  }
};

const AgentWaiting = () => {
  const { id } = useParams<{ id: string }>();

  const { agentType, agentSubType } = getAgentTypeFromId(id || "");
  const title = helper[agentSubType]?.title || "";

  const setOpenAgentLog = useAgentLogState.useActions().setIsOpen;
  const [downloading, setDownloading] = useState(false);
  const [isCopying, setIsCopying] = useState(false);
  const [showCopiedMessage, setShowCopiedMessage] = useState(false);
  const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false); // State for modal
  const [formLinkUrl, setFormLinkUrl] = useState("");
  const [isDiscarding, setIsDiscarding] = useState(false);

  const { mutate: sendReminder } = useSendThirdPartyVendorFormEmail();
  const { mutate: getFormLink, isPending: isGettingFormLink } = useGetVendorIntakeFormLink();
  const { mutate: discardSession } = useDiscardAgentSession();

  const { excelUrl, sessionName, sendReminderPayload } = useMemo(() => {
    if (!id)
      return {
        excelUrl: "",
        sessionName: "",
      };
    const agentData = getAgentData(id);
    const finalStep = agentData?.stepData?.find(
      (step) => step.type === AgentSessionStepType.PREPARE_REPORT,
    );

    const {multi_file_vendor_assessment} = agentData?.sessionData as any;

    const sendReminderPayload: SendThirdPartyVendorEmailPayload = {
      agent_session_id: id,
      vendor_email: multi_file_vendor_assessment.vendor_email as string,
      contact_email: multi_file_vendor_assessment.contact_email as string,
      vendor_name: multi_file_vendor_assessment.vendor_name,
      ui_redirect_url: `${window.location.origin}/vendor-security-form/`,
      request_type: 'reminder',
    }

    return {
      excelUrl: finalStep?.data?.url || "",
      sessionName: agentData?.sessionData.name || "",
      sendReminderPayload
    };
  }, [id]);

  
  const handleDownload = async () => {
    if (!excelUrl || !id) {
      addNotification({
        message: "Error in processing the file",
        type: "error",
      });
      return;
    }

    try {
      setDownloading(true);
      
      if (agentSubType === RiskAssessmentTypes.FAIR) {
        await generateAndDownloadSessionReport(id);
      } else {
        if (!excelUrl) {
          throw new Error("Download URL not found");
        }
        const agentData = getAgentData(id);
        const link = document.createElement("a");
        link.href = excelUrl;
        link.download = getFileNameForDownloadReport(agentData?.sessionData as AgentSession);
        link.click();
      }
    } catch (error) {
      console.error(error);
      addNotification({
        message: "Error in downloading the file",
        type: "error",
      });
    } finally {
      setDownloading(false);
    }
  };

  const navigate = useNavigate();

  const handleCopyLink = async () => {
    setIsCopying(true);
    
    if (!id || !sendReminderPayload?.vendor_email) {
      addNotification({
        message: "Missing required information to get form link",
        type: "error",
      });
      setIsCopying(false);
      return;
    }

    try {
      // Get the form link from the API
      getFormLink(
        {
          vendor_email: sendReminderPayload.vendor_email,
          agent_session_id: id
        },
        {
          onSuccess: async (response) => {
            if (response.success && response.data.vendor_intake_form_url) {
              const linkToCopy = response.data.vendor_intake_form_url;
              setFormLinkUrl(linkToCopy);
              
              // Copy the link to clipboard
              try {
                await navigator.clipboard.writeText(linkToCopy);
                setShowCopiedMessage(true);
                setTimeout(() => {
                  setShowCopiedMessage(false);
                }, 3000);
              } catch (error) {
                // Silently handle clipboard errors (e.g., document not focused)
                console.warn("Clipboard write failed:", error);
              }
            } else {
              throw new Error(response.message || "Failed to get form link");
            }
          },
          onError: (error) => {
            console.error("Failed to get form link: ", error);
            addNotification({
              message: "Failed to get form link. Please try again.",
              type: "error",
            });
          },
          onSettled: () => {
            setIsCopying(false);
          }
        }
      );
    } catch (err) {
      console.error("Failed to copy link: ", err);
      addNotification({
        message: "Failed to copy link. Please try again.",
        type: "error",
      });
      setIsCopying(false);
    }
  };

  const handleSendReminder = () => {
    sendReminder(sendReminderPayload as SendThirdPartyVendorEmailPayload)
    
    addNotification({
      message: "Reminder email sent",
      type: "success",
    });
  };

  const toggleDiscardModal = () => {
    setIsDiscardModalOpen(!isDiscardModalOpen);
  };

  const handleDiscardSession = async () => {
    if (!id) return;
    setIsDiscarding(true);
    
    try {
      discardSession(id, {
        onSuccess: (response) => {
          if (response.success) {
            addNotification({
              message: `Session discarded successfully`,
              type: "success",
            });
            navigate(`/${ROUTES.AGENT}`);
          } else {
            throw new Error(response.message || "Failed to discard session");
          }
        },
        onError: (error) => {
          console.error("Failed to discard session: ", error);
          addNotification({
            message: "Failed to discard session. Please try again.",
            type: "error",
          });
        },
        onSettled: () => {
          setIsDiscarding(false);
          setIsDiscardModalOpen(false);
        }
      });
    } catch (err) {
      console.error("Failed to discard session: ", err);
      addNotification({
        message: "Failed to discard session. Please try again.",
        type: "error",
      });
      setIsDiscarding(false);
      setIsDiscardModalOpen(false);
    }
  };

  const handleViewSession = async () => {
    if (!id) return;

    const agentData = getAgentData(id);
    if (!agentData) return;

    const { agentType, sessionData, stepData } = agentData;

    try {
      switch (agentType) {
        case AGENT_TYPES.RISK_ASSESSMENT:
          if(agentSubType === RiskAssessmentTypes.FAIR) {
            await handleContinueFairAssessment({
              session: sessionData as FairAgentSession,
              steps: stepData,
            });
          } else {
            await handleContinueRiskAssessment({
              session: sessionData as RiskAgentSession,
              steps: stepData,
            });
          }
          break;
        case AGENT_TYPES.QUESTIONNAIRE:
          await handleContinueQuestionnaire({
            session: sessionData as QaAgentSession,
            steps: stepData,
          });
          break;
        case AGENT_TYPES.GAP_ASSESSMENT:
          if ((sessionData as SOC2GapAgentSession).soc2_type2_audit) {
            await handleContinueSoc2GapAssessment({
              session: sessionData as SOC2GapAgentSession,
              steps: stepData,
            });
          } else if ((sessionData as DoraAgentSession).dora_assessment) {
            await handleContinueDoraGapAssessment({
              session: sessionData as DoraAgentSession,
              steps: stepData,
            });
          }
          break;
        case AGENT_TYPES.MULTI_FILE_VENDOR_ASSESSMENT:
          await handleContinueMultiVendorAssessment({
            session: sessionData as MultiVendorAgentSession,
            steps: stepData,
          });
          break;
        default:
          throw new Error("Invalid session type");
      }
      navigate(`/${ROUTES.AGENT}/${id}`);
    } catch (error) {
      console.error(error);
      addNotification({
        type: "error",
        title: "Error",
        message: "Failed to view session. Please try again.",
      });
    }
  };

  return (
    <div className="w-full h-full flex flex-col">
      {/* Changed to flex-col, items-center, justify-center, text-center and adjusted gap */}
      <div className="w-full h-full bg-white flex flex-col items-center justify-center px-24 gap-8 rounded-lg text-center">
        {/* Icon container moved above text container */}
        <div className="flex items-center justify-center">
          <StopwatchIcon className="w-64 h-64" />
        </div>
        {/* Text container */}
        <div className="w-full flex flex-col items-center justify-center gap-4"> 
          <span>
            An email with the risk questionnaire was sent to <a href={`mailto:${sendReminderPayload?.vendor_email}`} className="text-blue-600">{sendReminderPayload?.vendor_email}</a>.
            </span>
          <div>
            <span className="text-sm text-[#71717A] line-height-20">
              {`Your assessment will proceed automatically after the vendor completes their submission. You may send reminders as needed.`}
            </span>
          </div>
          <div className="mt-8 flex flex-row gap-3">
            <Button
              color="primary"
              variant="bordered"
              className="w-64 border-[1px] rounded-lg text-center" // Increased width from w-48 to w-64 for better loader display
              onClick={() => void handleCopyLink()}
              isLoading={isCopying || isGettingFormLink}
              disabled={showCopiedMessage} // Optionally disable button while "Copied" message is shown
            >
              {showCopiedMessage ? "Copied ✓" : "Copy Link to Form"}
            </Button>
            <div className="w-full flex flex-row gap-3">
              <Button
                color="primary"
                variant="bordered"
                className="w-full  border-[1px] rounded-lg"
                onClick={handleSendReminder} // Changed from handleViewSession
              >
                Send Reminder
              </Button>
              <Button
                color="danger"
                variant="bordered"
                className="w-full  border-[1px] rounded-lg"
                onClick={toggleDiscardModal} // Open modal instead of navigating directly
              >
                Discard Session
              </Button>
            </div>
          </div>
        </div>
      </div>
      {isDiscardModalOpen && (
        <DiscardSessionModal
          isOpen={isDiscardModalOpen}
          toggleModal={toggleDiscardModal}
          sessionName={sessionName}
          onConfirm={handleDiscardSession}
          isLoading={isDiscarding}
        />
      )}
    </div>
  );
};

export default AgentWaiting;
